import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Facebook, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { openWhatsApp, WHATSAPP_MESSAGES } from "@/lib/whatsapp";
import { OptimizedPicture } from "@/components/OptimizedPicture";
import GandaraLogo from "@/components/GandaraLogo";
import { LegalLinks } from "@/components/LegalLinks";

export const Footer = () => {
  const { language, translations } = useLanguage();
  const t = translations;
  const navigate = useNavigate();

  const getLocalizedPath = (path: string) => {
    const paths: Record<string, Record<string, string>> = {
      '/about': { es: '/quienes-somos', en: '/about', pt: '/sobre-nos', fr: '/a-propos' },
      '/montessori': { es: '/metodologia-montessori', en: '/montessori-education', pt: '/metodologia-montessori', fr: '/methode-montessori' },
      '/trilingual': { es: '/educacion-trilingue', en: '/trilingual-education', pt: '/educacao-trilingue', fr: '/education-trilingue' },
      '/ciencias': { es: '/ciencias', en: '/science', pt: '/ciencias', fr: '/sciences' },
      '/imagenes': { es: '/gandara-en-imagenes', en: '/gandara-in-pictures', pt: '/gandara-em-imagens', fr: '/gandara-en-images' },
      '/services': { es: '/servicios', en: '/services', pt: '/servicos', fr: '/services' },
      '/extracurriculars': { es: '/extraescolares', en: '/extracurriculars', pt: '/extracurriculares', fr: '/extrascolaires' },
      '/admissions': { es: '/admisiones', en: '/admissions', pt: '/admissoes', fr: '/admissions' },
      '/open-house-registration': { es: '/inscripcion-puertas-abiertas', en: '/open-house-registration', pt: '/inscricao-portas-abertas', fr: '/inscription-portes-ouvertes' },
      '/prices': { es: '/precios', en: '/prices', pt: '/precos', fr: '/tarifs' },
      '/birthdays': { es: '/cumpleanos', en: '/birthdays', pt: '/aniversarios', fr: '/anniversaires' },
      '/easter-camp': { es: '/campamento-semana-santa', en: '/easter-camp', pt: '/acampamento-pascoa', fr: '/camp-paques' },
      '/summer-camp': { es: '/campamento-verano', en: '/summer-camp', pt: '/acampamento-verao', fr: '/camp-ete' },
      '/courses': { es: '/cursos', en: '/courses', pt: '/cursos', fr: '/cours' }
    };
    return paths[path]?.[language] || path;
  };

  if (!t) return null;

  return (
    <footer className="bg-primary text-white py-12">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-8">
          {/* Column 1 - Branding */}
          <div className="lg:col-span-1">
            <button 
              onClick={() => navigate(`/${language}`)}
              className="mb-4 hover:opacity-90 transition-opacity"
            >
              <GandaraLogo tone="light" />
            </button>
            <p className="text-sm mb-4 opacity-90">{t.footer.slogan}</p>
            
            <div className="flex space-x-3 mb-6">
              <a 
                href="https://www.facebook.com/espaciogandara" 
                className="hover:text-accent transition-colors"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://www.instagram.com/gandaraischool/"
                className="hover:text-accent transition-colors"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>

            <a 
              href="https://www.neasc.org/institution/gandara-international-school"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <OptimizedPicture 
                src="/lovable-uploads/360748c0-43d3-4c73-930f-1365f0ac18f2.png"
                alt="NEASC Accredited"
                width={128}
                height={64}
                loading="lazy"
                decoding="async"
                className="h-16 w-auto bg-white p-2 rounded-lg hover:opacity-90 transition-opacity"
              />
            </a>
          </div>

          {/* Column 2 - El Colegio */}
          <div>
            <h4 className="text-lg font-semibold mb-4">{t.footer.theSchool}</h4>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => navigate(`/${language}${getLocalizedPath('/about')}`)}
                  className="hover:text-accent transition-colors text-left text-sm hover:underline"
                >
                  {t.footer.links.aboutUs}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate(`/${language}${getLocalizedPath('/montessori')}`)}
                  className="hover:text-accent transition-colors text-left text-sm hover:underline"
                >
                  {{ es: 'Método Gándara', en: 'The Gándara Method', pt: 'Método Gándara', fr: 'Méthode Gándara' }[language]}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate(`/${language}${getLocalizedPath('/trilingual')}`)}
                  className="hover:text-accent transition-colors text-left text-sm hover:underline"
                >
                  {t.footer.links.trilingual}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate(`/${language}${getLocalizedPath('/ciencias')}`)}
                  className="hover:text-accent transition-colors text-left text-sm hover:underline"
                >
                  {{ es: 'Ciencias', en: 'Science', pt: 'Ciências', fr: 'Sciences' }[language]}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate(`/${language}${getLocalizedPath('/imagenes')}`)}
                  className="hover:text-accent transition-colors text-left text-sm hover:underline"
                >
                  {{ es: 'Gándara en imágenes', en: 'Gándara in pictures', pt: 'Gándara em imagens', fr: 'Gándara en images' }[language]}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate(`/${language}${getLocalizedPath('/services')}`)}
                  className="hover:text-accent transition-colors text-left text-sm hover:underline"
                >
                  {t.footer.links.services}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate(`/${language}${getLocalizedPath('/extracurriculars')}`)}
                  className="hover:text-accent transition-colors text-left text-sm hover:underline"
                >
                  {t.footer.links.extracurriculars}
                </button>
              </li>
            </ul>
          </div>

          {/* Column 3 - Admisiones */}
          <div>
            <h4 className="text-lg font-semibold mb-4">{t.footer.admissions}</h4>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => navigate(`/${language}${getLocalizedPath('/open-house-registration')}`)}
                  className="hover:text-accent transition-colors text-left text-sm hover:underline font-medium"
                >
                  {t.footer.links.openHouse}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate(`/${language}${getLocalizedPath('/admissions')}`)}
                  className="hover:text-accent transition-colors text-left text-sm hover:underline"
                >
                  {t.footer.links.admissionProcess}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate(`/${language}${getLocalizedPath('/about')}`)}
                  className="hover:text-accent transition-colors text-left text-sm hover:underline"
                >
                  {t.footer.links.visitSchool}
                </button>
              </li>
            </ul>
          </div>

          {/* Column 4 - Actividades */}
          <div>
            <h4 className="text-lg font-semibold mb-4">{t.footer.activities}</h4>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => navigate(`/${language}${getLocalizedPath('/birthdays')}`)}
                  className="hover:text-accent transition-colors text-left text-sm hover:underline"
                >
                  {t.footer.links.birthdays}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate(`/${language}${getLocalizedPath('/easter-camp')}`)}
                  className="hover:text-accent transition-colors text-left text-sm hover:underline"
                >
                  {t.footer.links.easterCamp}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate(`/${language}${getLocalizedPath('/summer-camp')}`)}
                  className="hover:text-accent transition-colors text-left text-sm hover:underline"
                >
                  {t.footer.links.summerCamp}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate(`/${language}${getLocalizedPath('/courses')}`)}
                  className="hover:text-accent transition-colors text-left text-sm hover:underline"
                >
                  {t.footer.links.courses}
                </button>
              </li>
            </ul>
          </div>

          {/* Column 5 - Contacto */}
          <div>
            <h4 className="text-lg font-semibold mb-4">{t.footer.contact}</h4>
            <ul className="space-y-3 mb-6">
              <li>
                <a 
                  href={`tel:${t.footer.phone1.replace(/\s/g, '')}`}
                  className="hover:text-accent transition-colors flex items-center gap-2 text-sm hover:underline"
                >
                  <Phone className="w-4 h-4" />
                  {t.footer.phone1}
                </a>
              </li>
              <li>
                <a 
                  href={`tel:${t.footer.phone2.replace(/\s/g, '')}`}
                  className="hover:text-accent transition-colors flex items-center gap-2 text-sm hover:underline"
                >
                  <Phone className="w-4 h-4" />
                  {t.footer.phone2}
                </a>
              </li>
              <li>
                <a 
                  href="mailto:info@gandaraschool.com"
                  className="hover:text-accent transition-colors flex items-center gap-2 text-sm hover:underline"
                >
                  <Mail className="w-4 h-4" />
                  {t.footer.email}
                </a>
              </li>
              <li>
                <a 
                  href="https://maps.google.com/?q=Rúa+Moreira+1+Gondomar+Pontevedra+España"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-accent transition-colors flex items-start gap-2 text-sm hover:underline"
                >
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    {t.footer.address1}<br />
                    {t.footer.address2}
                  </span>
                </a>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  {t.footer.weekdays}<br />
                  {t.footer.hours}
                </span>
              </li>
            </ul>

            <Button 
              onClick={() => openWhatsApp(WHATSAPP_MESSAGES.bookVisit[language])}
              className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"
            >
              {t.footer.bookVisitCta}
            </Button>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-white/20 text-center">
          <p className="text-sm">
            © 2025 Gándara International School. {t.footer.allRightsReserved} ·{" "}
            <LegalLinks className="hover:text-accent transition-colors" />
          </p>
        </div>
      </div>
    </footer>
  );
};
